import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import axios from "axios"
import { Modal } from "components"
import { getBranchList, YOIBOI_OUTLET_CODE } from "helpers/general-helper"
import {
  apiBase,
  checkLegacyBukti,
  fetchGoodsReceiptForPo,
  fetchSatuanMap,
  normalizeText,
  parseLegacyDate,
  pickCostPrice,
  pickProductId,
  readFileAsText,
  resolveLegacyProductMappings,
  stripControlChars,
  todayIso,
  updateGoodsReceiptHeader,
} from "./import-utils"

// Parser file PRN "Surat Jalan" pabrik -> HO (PT. Atalla Indonesiah).
// Satu file bisa berisi banyak surat jalan sekaligus; tiap dokumen dikenali dari
// baris "No.Bukti : xx" dan item-nya berada di antara pembatas "====" setelah
// header kolom Kode / Tgl.Ord / Frame / Lensa / Jumlah / Sisa. Identitas produk
// = gabungan Kode + Frame + Lensa. Qty memakai satuan di footer dokumen
// ("Jumlah = 239.16 LSN"); desimalnya pecahan lusin (0.08 LSN ≈ 1 pcs).

// Kolom Frame fixed 16 karakter tepat setelah "dd/mm/yy ", jadi tanggal order
// dipakai sebagai anchor posisi. Lensa bisa mengandung spasi ("S2 GM"), maka
// batas ke kolom Jumlah dicari lewat pemisah >= 2 spasi (fallback 1 spasi).
const parseFactoryItemLine = (line) => {
  const dateMatch = line.match(/\s(\d{2}\/\d{2}\/\d{2})\s/)
  if (!dateMatch) return null

  const kode = line.slice(0, dateMatch.index).trim()
  const afterDate = line.slice(dateMatch.index + dateMatch[0].length)
  const frame = afterDate.slice(0, 16).trim()
  const rest = afterDate.slice(16)

  const restMatch = rest.match(/^(.*?)\s{2,}([\d.,]+)\s+([\d.,]+)\s*$/)
    || rest.match(/^(.+?)\s+([\d.,]+)\s+([\d.,]+)\s*$/)
  if (!restMatch) return null

  const lensa = restMatch[1].trim()
  const qty = Number(restMatch[2].replace(/,/g, ""))

  if (!kode || !Number.isFinite(qty) || qty <= 0) return null

  return {
    kode,
    frame,
    lensa,
    qty,
    productName: [kode, frame, lensa].filter(Boolean).join(" ").replace(/\s+/g, " ").trim(),
  }
}

const parseFactorySuratJalan = (text) => {
  const lines = String(text || "").replace(/\r/g, "\n").split("\n")

  const docs = []
  let current = null
  let insideItems = false

  for (const rawLine of lines) {
    const line = stripControlChars(rawLine).trimEnd()

    const noBuktiMatch = line.match(/No\.Bukti\s*:\s*(\S+)/i)
    if (noBuktiMatch) {
      current = {
        noBukti: noBuktiMatch[1],
        date: parseLegacyDate(line.match(/Tanggal\s*:\s*([\d/]+)/i)?.[1] || ""),
        keterangan: "",
        unit: "LSN",
        items: [],
      }
      docs.push(current)
      insideItems = false
      continue
    }

    if (!current) continue

    const ketMatch = line.match(/KETERANGAN\s*:\s*(.*?)\s*(?:Hal\s*:.*)?$/i)
    if (ketMatch) {
      if (!current.keterangan) current.keterangan = ketMatch[1].trim()
      continue
    }

    if (/^\s*Kode\s+Tgl\.?\s*Ord\s+Frame\s+Lensa\s+Jumlah\s+Sisa/i.test(line)) {
      insideItems = true
      continue
    }

    // Layout surat jalan CUSTOMER (Kode Barang / Jumlah / Satuan — seri 26A4xxx
    // dst.) — bukan surat jalan pabrik. Tandai supaya warning bisa mengarahkan
    // user ke tombol "Import Surat Jalan" (DO lama), bukan sekadar "tidak terbaca".
    if (/^\s*Kode Barang\s+Jumlah\s+Satuan/i.test(line)) {
      current.customerDoLayout = true
      continue
    }

    const totalMatch = line.match(/Jumlah\s*=\s*[\d.,]+\s+(\S+)/i)
    if (totalMatch) {
      current.unit = totalMatch[1].toUpperCase()
      insideItems = false
      continue
    }

    if (!insideItems) continue
    if (!line.trim() || /^=+$/.test(line.trim())) continue

    const item = parseFactoryItemLine(line)
    if (item) current.items.push(item)
  }

  // Dokumen multi-halaman mengulang header No.Bukti yang sama -> gabungkan,
  // lalu item duplikat (kode+frame+lensa sama) di-merge qty-nya.
  const byNoBukti = new Map()
  for (const doc of docs) {
    const key = normalizeText(doc.noBukti) || `#${byNoBukti.size}`
    const existing = byNoBukti.get(key)
    if (existing) {
      existing.items.push(...doc.items)
      if (!existing.keterangan) existing.keterangan = doc.keterangan
      if (doc.customerDoLayout) existing.customerDoLayout = true
    } else {
      byNoBukti.set(key, doc)
    }
  }

  return Array.from(byNoBukti.values()).map((doc) => ({
    ...doc,
    items: Array.from(
      doc.items
        .reduce((map, item) => {
          const key = normalizeText(item.productName)
          const currentItem = map.get(key)
          map.set(key, currentItem ? { ...currentItem, qty: currentItem.qty + item.qty } : item)
          return map
        }, new Map())
        .values()
    ),
  }))
}

const docTotalQty = (doc) => doc.items.reduce((sum, item) => sum + item.qty, 0)

const resultBadge = (status) => {
  const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium"
  if (status === "created") return <span className={`${base} bg-green-50 text-green-700`}>Berhasil</span>
  if (status === "skipped") return <span className={`${base} bg-amber-50 text-amber-700`}>Dilewati</span>
  return <span className={`${base} bg-red-50 text-red-700`}>Gagal</span>
}

// Import Surat Jalan pabrik khusus entity ATALLA & VISION: satu PO (po_source
// FACTORY) + GR draft per No.Bukti di dalam file.
export default function ImportFactorySuratJalan({ currentBranch, supplierSelection, userName, onSuccess }) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [fileName, setFileName] = useState("")
  const [docs, setDocs] = useState([])
  const [outletId, setOutletId] = useState("")
  const [supplierId, setSupplierId] = useState("")
  const [errors, setErrors] = useState([])
  const [warnings, setWarnings] = useState([])
  const [results, setResults] = useState([])
  const [submitting, setSubmitting] = useState(false)
  // ATALLA-323: nomor PO manual per dokumen (keyed No.Bukti) — kosong = otomatis.
  const [poNumbers, setPoNumbers] = useState({})

  // ATALLA-347: outlet penerima surat jalan pabrik = entity ATALLA/VISION (whitelist
  // eksplisit). Sumber daftar = FETCH SEMUA branch dari generic API, BUKAN localStorage
  // 'branch_list' — untuk user non-super cache itu diisi /branch/by?user_id (hanya branch
  // yang di-assign ke user), sehingga PT Atalla Indonesia (HO) tidak pernah muncul sebagai
  // penerima. Fallback ke cache lama saat fetch gagal/berjalan. Outlet konsinyasi yoiboi
  // tetap disembunyikan; branch tanpa entity (pra-migrasi) tetap tampil agar aman.
  // 150726 (ATALLA-347): daftar TANPA restriksi per-user ini aman dipakai di sini karena
  // tombol import hanya dirender utk pemegang permission purchase-order.create
  // (lihat purchase/list.jsx) — user toko biasa tanpa permission itu tidak pernah
  // melihat form ini, jadi restriksi outlet per-user utk mereka tidak dibongkar.
  const [allBranches, setAllBranches] = useState(null)
  useEffect(() => {
    let mounted = true
    axios.get(`${process.env.REACT_APP_GENERIC_API_URL}/branch`, { params: { paginate: false } })
      .then((response) => { if (mounted) setAllBranches(response?.data?.data) })
      .catch(() => {})
    return () => { mounted = false }
  }, [])

  const eligibleBranches = useMemo(() => {
    const list = (Array.isArray(allBranches) && allBranches.length > 0 ? allBranches : getBranchList()) || []
    if (!Array.isArray(list)) return []
    return list.filter((branch) => {
      if (String(branch?.code ?? "").toLowerCase() === YOIBOI_OUTLET_CODE) return false
      return !branch?.entity || ["ATALLA", "VISION"].includes(String(branch.entity).toUpperCase())
    })
  }, [allBranches])

  // 220726 (permintaan user): dropdown supplier TIDAK difilter — supplier internal
  // (mis. Vision One) sah dipakai di import. Approve dari flow import tidak lagi
  // memotong stok sisi supplier internal (skip_ho_outflow); potong stok terjadi
  // saat GR di-post.
  const factorySuppliers = useMemo(
    () => (Array.isArray(supplierSelection) ? supplierSelection : []),
    [supplierSelection]
  )

  const defaultOutletId = useMemo(() => {
    const currentId = String(currentBranch?.id ?? "")
    return eligibleBranches.some((branch) => String(branch.id) === currentId) ? currentId : ""
  }, [eligibleBranches, currentBranch])

  const reset = () => {
    setFileName("")
    setDocs([])
    setErrors([])
    setWarnings([])
    setResults([])
    setSubmitting(false)
    setPoNumbers({})
    setOutletId(defaultOutletId)
    setSupplierId("")
  }

  const openModal = () => {
    reset()
    setIsOpen(true)
  }

  const closeModal = () => {
    if (submitting) return
    setIsOpen(false)
  }

  const onFileChange = async (event) => {
    const file = event.target.files?.[0]
    setErrors([])
    setWarnings([])
    setResults([])
    setDocs([])
    setPoNumbers({})
    setFileName(file?.name || "")

    if (!file) return

    try {
      const text = await readFileAsText(file)
      const parsedDocs = parseFactorySuratJalan(text)

      if (!parsedDocs.length) {
        setErrors(["Surat jalan tidak terbaca. Pastikan file berisi header No.Bukti dan tabel Kode / Frame / Lensa / Jumlah."])
        return
      }

      const emptyDocs = parsedDocs.filter((doc) => !doc.items.length)
      if (emptyDocs.length) {
        setWarnings(emptyDocs.map((doc) => (doc.customerDoLayout
          ? `No.Bukti ${doc.noBukti}: ini surat jalan CUSTOMER (kolom Kode Barang / Jumlah / Satuan), bukan surat jalan pabrik — import lewat tombol "Import Surat Jalan" (DO lama). Dokumen dilewati.`
          : `No.Bukti ${doc.noBukti}: item tidak terbaca, dokumen dilewati.`)))
      }

      setDocs(parsedDocs.filter((doc) => doc.items.length))
    } catch (error) {
      setErrors([error?.message || "Gagal membaca file surat jalan"])
    }
  }

  // Resolve mapping + konversi satuan untuk seluruh dokumen sekaligus.
  // All-or-nothing: kalau ada produk yang belum di-mapping, tidak ada PO dibuat.
  const buildAllItems = async (pendingDocs) => {
    const notFound = new Set()
    const warningSet = new Set()

    const [mappingMap, satuanMap] = await Promise.all([
      resolveLegacyProductMappings(
        pendingDocs.flatMap((doc) => doc.items.map((item) => item.productName)),
        outletId
      ),
      fetchSatuanMap(),
    ])

    const factorFor = (unit) => {
      const key = normalizeText(unit || "LSN")
      const factor = satuanMap.get(key)
      if (Number.isFinite(factor) && factor > 0) return factor
      if (key === "LSN") {
        warningSet.add(`Satuan "LSN" tidak ada di master — dipakai konversi 12 pcs/lusin.`)
        return 12
      }
      warningSet.add(`Satuan "${unit}" tidak ada di master — dianggap 1:1 ke pcs.`)
      return 1
    }

    const perDoc = pendingDocs.map((doc) => {
      const resolved = []

      for (const item of doc.items) {
        const mapping = mappingMap.get(normalizeText(item.productName))

        if (!mapping || !pickProductId(mapping)) {
          notFound.add(item.productName)
          continue
        }

        if (!pickCostPrice(mapping)) {
          warningSet.add(`Harga active belum ada: ${item.productName}`)
        }

        // Konversi lusin (atau satuan footer lain) ke pcs, dibulatkan ke integer
        // (mis. 9.08 LSN × 12 = 108.96 → 109 PCS).
        const qtyInPcs = Math.round(Number(item.qty) * factorFor(doc.unit))
        if (qtyInPcs <= 0) {
          warningSet.add(`No.Bukti ${doc.noBukti}: qty jadi 0 pcs setelah konversi, item dilewati — ${item.productName} (${item.qty} ${doc.unit})`)
          continue
        }

        resolved.push({
          product_unit_id: String(pickProductId(mapping)),
          uom: "PCS",
          qty_ordered: qtyInPcs,
          qty_received: 0,
          cost_price: pickCostPrice(mapping),
          sell_price_ref: Number(mapping?.sell_price_ref ?? 0),
        })
      }

      return Array.from(
        resolved
          .reduce((map, item) => {
            const currentItem = map.get(item.product_unit_id)
            map.set(
              item.product_unit_id,
              currentItem
                ? { ...currentItem, qty_ordered: Number(currentItem.qty_ordered) + Number(item.qty_ordered) }
                : item
            )
            return map
          }, new Map())
          .values()
      )
    })

    return { perDoc, notFound: Array.from(notFound), matchWarnings: Array.from(warningSet) }
  }

  const createPo = async (doc, items) => {
    const response = await fetch(`${apiBase()}/purchase-orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        outlet_id: Number(outletId),
        supplier_id: supplierId,
        // ATALLA-323: nomor PO manual per dokumen (penamaan khusus); kosong = digenerate.
        po_number: (poNumbers[doc.noBukti] || "").trim() || undefined,
        // ATALLA-333: kalau nomor PO dikosongkan, backend generate format khusus
        // import pabrik: LP-<bulan>-<tahun>-<urut> (mis. LP-07-2026-0001).
        po_number_scheme: "FACTORY_LP",
        po_date: doc.date || todayIso(),
        po_type: "REGULAR",
        po_source: "FACTORY",
        status: "DRAFT",
        remarks: [
          "Import Surat Jalan Pabrik",
          // Baris "No.Bukti: x" harus berdiri sendiri: dipakai backend untuk deteksi duplikat.
          doc.noBukti ? `No.Bukti: ${doc.noBukti}` : null,
          doc.keterangan ? `Keterangan: ${doc.keterangan}` : null,
        ].filter(Boolean).join("\n"),
        createdBy: userName,
        items,
      }),
    })

    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      const fieldErrors = json?.errors && typeof json.errors === "object"
        ? Object.values(json.errors).flat().filter(Boolean).join("; ")
        : ""
      throw new Error(fieldErrors || json?.message || "Gagal membuat PO")
    }
    return json
  }

  const approvePo = async (poId) => {
    const response = await fetch(`${apiBase()}/purchase-orders/${poId}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // skip_ho_outflow: approve dari import TIDAK memotong stok sisi supplier
      // internal (Vision/HO) — potong stok terjadi saat GR di-post.
      // seed_full_received: GR draft hasil import tetap terisi qty dokumen
      // (default approve biasa kini seed 0 — diisi gudang di detail GR).
      body: JSON.stringify({ approvedBy: userName, skip_ho_outflow: true, seed_full_received: true }),
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(json?.message || "PO dibuat, tapi gagal approve")
    return json
  }

  const submitImport = async (event) => {
    event.preventDefault()
    setErrors([])
    setWarnings([])
    setResults([])

    if (!outletId) {
      toast.error("Pilih outlet terlebih dahulu")
      return
    }

    if (!supplierId) {
      toast.error("Pilih supplier terlebih dahulu")
      return
    }

    if (!docs.length) {
      toast.error("Upload file surat jalan terlebih dahulu")
      return
    }

    try {
      setSubmitting(true)
      nProgress.start()

      const skipped = []
      const pending = []

      for (const doc of docs) {
        const check = doc.noBukti ? await checkLegacyBukti(doc.noBukti) : null
        if (check?.exists) {
          const label = check?.po?.po_number ? ` (PO ${check.po.po_number})` : ""
          skipped.push({ noBukti: doc.noBukti, status: "skipped", message: `Sudah pernah diimport${label}` })
        } else {
          pending.push(doc)
        }
      }

      if (!pending.length) {
        setResults(skipped)
        setErrors(["Semua No.Bukti pada file ini sudah pernah diimport."])
        return
      }

      const { perDoc, notFound, matchWarnings } = await buildAllItems(pending)

      if (matchWarnings.length) setWarnings(matchWarnings)

      if (notFound.length) {
        setResults(skipped)
        setErrors(notFound.map((name) => `Mapping produk lama belum dibuat: ${name}`))
        return
      }

      const outcomes = [...skipped]

      for (let index = 0; index < pending.length; index++) {
        const doc = pending[index]

        try {
          const items = perDoc[index]
          if (!items.length) throw new Error("Tidak ada item valid setelah konversi satuan")

          const poJson = await createPo(doc, items)
          const poId = poJson?.data?.id || poJson?.id
          if (!poId) throw new Error("PO berhasil dibuat, tapi ID PO tidak ditemukan dari response")

          await approvePo(poId)

          const receipt = await fetchGoodsReceiptForPo(poId)
          if (!receipt?.id) throw new Error("PO sudah approved, tapi Goods Receipt draft tidak ditemukan")

          try {
            await updateGoodsReceiptHeader(receipt.id, {
              receipt_date: doc.date || todayIso(),
              supplier_do_number: doc.noBukti || null,
              remarks: [
                doc.noBukti ? `Import Surat Jalan Pabrik No.Bukti: ${doc.noBukti}` : null,
                doc.keterangan ? `Keterangan: ${doc.keterangan}` : null,
              ].filter(Boolean).join("\n") || null,
            })
          } catch (headerError) {
            toast.error(`No.Bukti ${doc.noBukti}: GR dibuat, tapi header belum ter-update`)
          }

          outcomes.push({
            noBukti: doc.noBukti,
            status: "created",
            poNumber: poJson?.data?.po_number || poJson?.po_number || "",
            grId: receipt.id,
          })
        } catch (docError) {
          outcomes.push({ noBukti: doc.noBukti, status: "failed", message: docError?.message || "Gagal memproses dokumen" })
        }

        setResults([...outcomes])
      }

      const created = outcomes.filter((o) => o.status === "created")
      const failed = outcomes.filter((o) => o.status === "failed")

      if (created.length) onSuccess?.()

      if (failed.length) {
        toast.error(`${created.length} surat jalan berhasil, ${failed.length} gagal. Cek detail di bawah.`)
      } else if (created.length) {
        toast.success(`${created.length} surat jalan diimport. PO approved dan Goods Receipt draft dibuat.`)
      }

      // File berisi satu dokumen bersih -> langsung buka GR-nya, sama seperti import DO lama.
      if (created.length === 1 && !failed.length && !skipped.length) {
        setIsOpen(false)
        navigate(`/goods-receipt/${created[0].grId}/edit`)
      }
    } catch (error) {
      setErrors([error?.message || "Gagal import surat jalan"])
      toast.error(error?.message || "Gagal import surat jalan")
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
      >
        <span>Import SJ Pabrik</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title="Import Surat Jalan Pabrik (Atalla / Vision)"
        content={(
          <form onSubmit={submitImport} className="mt-6 space-y-5 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Outlet Penerima</label>
                <select
                  value={outletId || ""}
                  onChange={(event) => setOutletId(event.target.value)}
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                >
                  <option value="">Select Outlet</option>
                  {eligibleBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}{branch.entity ? ` (${branch.entity})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Supplier (Pabrik)</label>
                <select
                  value={supplierId || ""}
                  onChange={(event) => setSupplierId(event.target.value)}
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                >
                  <option value="">Select Supplier</option>
                  {factorySuppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="block font-medium text-neutral-700">File Surat Jalan (PRN)</label>
              <input
                type="file"
                accept=".prn,.PRN,.txt"
                onChange={onFileChange}
                className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-brand-800 file:text-white hover:file:bg-brand-900"
              />
              {fileName && <p className="text-[11px] text-neutral-500">Selected file: <span className="font-medium">{fileName}</span></p>}
              <p className="text-[11px] text-neutral-400">Satu file bisa berisi beberapa surat jalan — tiap No.Bukti dibuatkan satu PO + Goods Receipt draft.</p>
            </div>

            {docs.length > 0 && (
              <div className="space-y-3 max-h-72 overflow-auto pr-1">
                {docs.map((doc) => (
                  <div key={doc.noBukti} className="overflow-hidden border border-neutral-200 rounded-xl">
                    <div className="grid grid-cols-2 gap-2 p-3 bg-neutral-50 text-[11px] text-neutral-700">
                      <div>No.Bukti: <b>{doc.noBukti || "-"}</b></div>
                      <div>Tanggal: <b>{doc.date || "-"}</b></div>
                      <div>Keterangan: <b>{doc.keterangan || "-"}</b></div>
                      <div>Items: <b>{doc.items.length}</b> — Total: <b>{docTotalQty(doc).toFixed(2)} {doc.unit}</b></div>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="whitespace-nowrap">Nomor PO:</span>
                        <input
                          type="text"
                          value={poNumbers[doc.noBukti] || ""}
                          onChange={(e) => setPoNumbers((prev) => ({ ...prev, [doc.noBukti]: e.target.value }))}
                          placeholder="Kosongkan untuk nomor otomatis"
                          disabled={submitting}
                          className="flex-1 px-2 py-1.5 text-[11px] border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200 disabled:bg-neutral-100"
                        />
                      </div>
                    </div>
                    <div className="max-h-40 overflow-auto">
                      <table className="min-w-full divide-y divide-neutral-200">
                        <thead className="bg-white">
                          <tr>
                            <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Kode</th>
                            <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Frame</th>
                            <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Lensa</th>
                            <th className="px-3 py-2 text-[11px] font-medium text-right text-neutral-500 uppercase">Qty ({doc.unit})</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                          {doc.items.slice(0, 50).map((item) => (
                            <tr key={item.productName} className="hover:bg-brand-50 transition-colors">
                              <td className="px-3 py-2 text-[11px] text-neutral-800">{item.kode}</td>
                              <td className="px-3 py-2 text-[11px] text-neutral-800">{item.frame || "-"}</td>
                              <td className="px-3 py-2 text-[11px] text-neutral-800">{item.lensa || "-"}</td>
                              <td className="px-3 py-2 text-[11px] text-right text-neutral-800">{item.qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {doc.items.length > 50 && (
                        <p className="px-3 py-2 text-[11px] text-neutral-500">+{doc.items.length - 50} item lainnya…</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {results.length > 0 && (
              <div className="overflow-hidden border border-neutral-200 rounded-xl">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">No.Bukti</th>
                      <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {results.map((row) => (
                      <tr key={row.noBukti}>
                        <td className="px-3 py-2 text-[11px] font-medium text-neutral-800">{row.noBukti || "-"}</td>
                        <td className="px-3 py-2 text-[11px]">{resultBadge(row.status)}</td>
                        <td className="px-3 py-2 text-[11px] text-neutral-700">
                          {row.status === "created" ? (
                            <span>
                              {row.poNumber ? `PO ${row.poNumber} — ` : ""}
                              <Link to={`/goods-receipt/${row.grId}/edit`} className="font-semibold underline">Buka GR</Link>
                            </span>
                          ) : (row.message || "-")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="p-3 text-[11px] border rounded-xl border-amber-200 bg-amber-50 text-amber-800 max-h-32 overflow-auto">
                <div className="mb-1 font-semibold">Perhatian:</div>
                <ul className="pl-4 space-y-1 list-disc">
                  {warnings.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
              </div>
            )}

            {errors.length > 0 && (
              <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
                <div className="mb-1 font-semibold">Error:</div>
                <ul className="pl-4 space-y-1 list-disc">
                  {errors.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-3 text-xs font-semibold text-white bg-brand-800 rounded-xl hover:bg-brand-900 disabled:opacity-60"
              >
                {submitting ? "Memproses..." : `Import ${docs.length || ""} Surat Jalan`.replace("  ", " ")}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={closeModal}
                className="px-5 py-3 text-xs font-semibold border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
              >
                {results.some((row) => row.status === "created") ? "Tutup" : "Cancel"}
              </button>
            </div>
          </form>
        )}
      />
    </>
  )
}
